import React, { useEffect, useMemo, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import {
  IconSpeakerphone,
  IconPlus,
  IconPencil,
  IconTrash,
  IconCalendarCheck,
  IconCalendarEvent,
  IconRepeat,
} from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useLoading } from '../context/LoadingContext';
import { todayDateStringInTz, DAY_ANCHOR_TZ } from '../utils/time';
import { announcementStatus, formatRecurrenceLabel, formatVaDateLabel, markAnnouncementsSeen } from '../utils/announcements';
import Layout from '../components/Layout';
import SelectField from '../components/SelectField';
import ConfirmModal from '../components/ConfirmModal';

const WRITES_TO_SHEET = Boolean(process.env.REACT_APP_APPS_SCRIPT_URL);

const RECURRENCE_OPTIONS = [
  { value: 'one_time', label: 'One-time' },
  { value: 'annual', label: 'Annual' },
  { value: 'interval', label: 'Recurring' },
];

const EMPTY_FORM = {
  message: '',
  recurrenceType: 'one_time',
  endDate: '',
  anchorDate: '', // a full date the admin picks — only its month/day is used, for 'annual'
  startDate: '',
  intervalDays: '14',
  leadDays: '0',
  active: true,
};

// Same inline re-auth pattern as Leaves/Check In-Out — a quick Google
// sign-in right in the modal footer when the token's gone stale, instead of
// sending the admin off to find a sign-in button elsewhere.
function ReauthFooter({ setIdToken, onCancel }) {
  const { showError } = useToast();
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-sm text-muted text-center">Your session needs a quick refresh before this can be saved.</p>
      <GoogleLogin
        onSuccess={(credentialResponse) => setIdToken(credentialResponse.credential)}
        onError={() => showError('Google sign-in failed — please try again.')}
      />
      <button onClick={onCancel} className="text-sm font-medium text-muted hover:text-ink transition-colors">
        Cancel
      </button>
    </div>
  );
}

function RecurrenceIcon({ type }) {
  if (type === 'annual') return <IconCalendarEvent size={18} stroke={1.8} className="text-bronze" />;
  if (type === 'interval') return <IconRepeat size={18} stroke={1.8} className="text-bronze" />;
  return <IconCalendarCheck size={18} stroke={1.8} className="text-bronze" />;
}

export default function Announcements() {
  const {
    currentUser,
    announcements,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    setIdToken,
    isIdTokenValid,
  } = useApp();
  const { showSuccess, showError } = useToast();
  const { withLoading } = useLoading();
  const needsReauth = WRITES_TO_SHEET && !isIdTokenValid;
  const isAdmin = Boolean(currentUser && currentUser.isAdmin);

  const todayCst = useMemo(() => todayDateStringInTz(DAY_ANCHOR_TZ), []);

  // Clears the Sidebar's unread badge the moment this page is opened.
  useEffect(() => {
    markAnnouncementsSeen();
  }, []);

  // Admins see everything (active ones first, so the list still reads as
  // "what's live" at a glance) so they can manage paused/expired rows too.
  // VAs only ever see currently-active ones.
  const visible = useMemo(() => {
    const decorated = announcements.map((a) => {
      const status = announcementStatus(a, todayCst);
      return { ...a, daysUntil: status.daysUntil, currentlyActive: a.active !== false && status.isActiveToday };
    });
    if (isAdmin) {
      return decorated.sort((x, y) => {
        if (x.currentlyActive !== y.currentlyActive) return x.currentlyActive ? -1 : 1;
        return (x.daysUntil || 0) - (y.daysUntil || 0);
      });
    }
    return decorated.filter((a) => a.currentlyActive).sort((x, y) => (x.daysUntil || 0) - (y.daysUntil || 0));
  }, [announcements, todayCst, isAdmin]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = creating new
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null); // announcement_id | null

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (a) => {
    setEditingId(a.announcement_id);
    setForm({
      message: a.message,
      recurrenceType: a.recurrence_type,
      endDate: a.end_date || '',
      anchorDate: a.month && a.day ? `2001-${String(a.month).padStart(2, '0')}-${String(a.day).padStart(2, '0')}` : '',
      startDate: a.start_date || '',
      intervalDays: a.interval_days ? String(a.interval_days) : '14',
      leadDays: a.lead_days != null ? String(a.lead_days) : '0',
      active: a.active !== false,
    });
    setFormOpen(true);
  };

  const closeForm = () => setFormOpen(false);

  const buildFields = () => {
    const fields = { message: form.message.trim(), recurrenceType: form.recurrenceType };
    if (form.recurrenceType === 'one_time') {
      fields.endDate = form.endDate;
    } else if (form.recurrenceType === 'annual') {
      const [, m, d] = form.anchorDate.split('-');
      fields.month = Number(m);
      fields.day = Number(d);
      fields.leadDays = Number(form.leadDays) || 0;
    } else {
      fields.startDate = form.startDate;
      fields.intervalDays = Number(form.intervalDays);
      fields.leadDays = Number(form.leadDays) || 0;
    }
    return fields;
  };

  const confirmSave = async () => {
    if (!form.message.trim()) {
      showError('Write a message first.');
      return;
    }
    if (form.recurrenceType === 'one_time' && !form.endDate) {
      showError('Pick an expiration date.');
      return;
    }
    if (form.recurrenceType === 'annual' && !form.anchorDate) {
      showError('Pick which date this repeats on each year.');
      return;
    }
    if (form.recurrenceType === 'interval' && (!form.startDate || !form.intervalDays)) {
      showError('Pick a start date and how many days it repeats every.');
      return;
    }

    const fields = buildFields();
    try {
      if (editingId) {
        await withLoading(() => updateAnnouncement({ announcementId: editingId, fields, active: form.active }));
        showSuccess('Announcement updated.');
      } else {
        await withLoading(() => createAnnouncement(fields));
        showSuccess('Announcement posted.');
      }
      closeForm();
    } catch (err) {
      showError(err.message || "Couldn't save that — please try again.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await withLoading(() => deleteAnnouncement({ announcementId: deleteTarget }));
      showSuccess('Announcement deleted.');
      setDeleteTarget(null);
    } catch (err) {
      showError(err.message || "Couldn't delete that — please try again.");
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-ink flex items-center gap-2">
              <IconSpeakerphone size={22} stroke={1.8} className="text-gold" />
              Announcements
            </h1>
            <p className="text-sm text-muted mt-0.5">
              {isAdmin
                ? 'Company-wide announcements — active ones show in the banner at the top.'
                : "What's currently active for the team."}
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={openCreate}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-semibold text-white bg-gold hover:bg-bronze transition-colors text-sm whitespace-nowrap flex-shrink-0"
            >
              <IconPlus size={16} stroke={2} />
              New
            </button>
          )}
        </div>

        <div className="mt-6 space-y-3">
          {visible.length === 0 && <p className="text-sm text-muted text-center py-10">No announcements right now.</p>}
          {visible.map((a) => (
            <div
              key={a.announcement_id}
              className={`bg-card border border-border rounded-xl p-4 ${!a.currentlyActive ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-light-gold flex items-center justify-center">
                  <RecurrenceIcon type={a.recurrence_type} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ink whitespace-pre-wrap">{a.message}</p>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center rounded-full font-medium px-2.5 py-0.5 text-[11px] tracking-wide bg-light-gold text-bronze">
                      {isAdmin ? formatRecurrenceLabel(a) : formatVaDateLabel(a, todayCst)}
                    </span>
                    {!a.currentlyActive && (
                      <span className="inline-flex items-center rounded-full font-medium px-2.5 py-0.5 text-[11px] tracking-wide bg-neutral-bg text-neutral-tx">
                        {a.active === false ? 'Paused' : 'Not active'}
                      </span>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex-shrink-0 flex items-center gap-1">
                    <button
                      onClick={() => openEdit(a)}
                      aria-label="Edit"
                      className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors"
                    >
                      <IconPencil size={16} stroke={1.8} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(a.announcement_id)}
                      aria-label="Delete"
                      className="p-1.5 rounded-lg text-muted hover:text-error-tx hover:bg-card-tint transition-colors"
                    >
                      <IconTrash size={16} stroke={1.8} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ConfirmModal
        open={formOpen}
        title={editingId ? 'Edit announcement' : 'New announcement'}
        confirmLabel="Save"
        onConfirm={confirmSave}
        onCancel={closeForm}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={closeForm} /> : undefined}
      >
        <div>
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Message</label>
          <textarea
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            rows={3}
            placeholder="What should the team see?"
            className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
          />
        </div>

        <div className="mt-3">
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Type</label>
          <SelectField
            value={form.recurrenceType}
            onChange={(e) => setForm((f) => ({ ...f, recurrenceType: e.target.value }))}
          >
            {RECURRENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </SelectField>
        </div>

        {form.recurrenceType === 'one_time' && (
          <div className="mt-3">
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Shows until</label>
            <input
              type="date"
              value={form.endDate}
              min={todayCst}
              onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
              className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
            />
          </div>
        )}

        {form.recurrenceType === 'annual' && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Repeats every</label>
              <input
                type="date"
                value={form.anchorDate}
                onChange={(e) => setForm((f) => ({ ...f, anchorDate: e.target.value }))}
                className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
              />
              <p className="text-[11px] text-muted mt-1">Only the month/day is used — the year doesn't matter.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Lead time (days)</label>
              <input
                type="number"
                min="0"
                value={form.leadDays}
                onChange={(e) => setForm((f) => ({ ...f, leadDays: e.target.value }))}
                className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
              />
            </div>
          </div>
        )}

        {form.recurrenceType === 'interval' && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Starts</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Every (days)</label>
              <input
                type="number"
                min="1"
                value={form.intervalDays}
                onChange={(e) => setForm((f) => ({ ...f, intervalDays: e.target.value }))}
                className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Lead time (days)</label>
              <input
                type="number"
                min="0"
                value={form.leadDays}
                onChange={(e) => setForm((f) => ({ ...f, leadDays: e.target.value }))}
                className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
              />
            </div>
          </div>
        )}

        {editingId && (
          <label className="mt-3 flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
            />
            Active
          </label>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Delete this announcement?"
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={() => setDeleteTarget(null)} /> : undefined}
      >
        <p>This can't be undone.</p>
      </ConfirmModal>
    </Layout>
  );
}
